import { usePostExternalLogin } from '@framework/api-client/api/auth/esterni-controller/esterni-controller.ts';
import { type LoginResponse } from '@framework/api-client/api/auth/schemas/loginResponse.ts';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Link } from '@framework/ui/core/router/TypedRoute.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getNetworkErrorMessage } from '../../http/errors/getNetworkErrorMessage.ts';

const LoginSchema = yup
  .object({
    email: yup
      .string()
      .email('Should be a valid email address')
      .required('E-Mail is a required field')
      .label('Email'),
    password: yup
      .string()
      .required('Password is a required field')
      .label('Password'),
  })
  .required()
  .noUnknown();

export interface IExternalUserLoginFormProps {
  onLogin: (result: LoginResponse) => void;
  hideInfos?: boolean;
  submitButtonText?: string;
}

/**
 * Form di login per gli utenti esterni (username+psw)
 */
export function ExternalUserLoginForm(props: IExternalUserLoginFormProps) {
  const { onLogin, hideInfos, submitButtonText } = props;

  const login = usePostExternalLogin();

  const { t } = useTranslation();

  return (
    <FormHandler
      schema={LoginSchema}
      onSubmit={async values => {
        try {
          const result = await login.mutateAsync({
            data: values,
          });

          onLogin(result);
        } catch (e) {
          // biome-ignore format: <tbd>
          message.error(getNetworkErrorMessage(e, 'Si è verificato un errore durante il login.'));
          console.error(`[LoginForm] Errore durante il login`, { error: e });
        }
      }}
      hideVisualFeedback
    >
      <FormItem
        label={t('user.login.external-user-email', 'Email')}
        name="email"
        required
      >
        <TextInput
          name="email"
          placeholder={t('user.login.external-user-email', 'Email')}
        />
      </FormItem>
      <FormItem
        label={t('user.login.external-user-password', 'Password')}
        name="password"
        required
      >
        <TextInput
          name="password"
          placeholder={t('user.login.external-user-password', 'Password')}
          type="password"
        />
      </FormItem>

      <SubmitButton
        type="primary"
        style={{ width: '100%' }}
        pristineMessage={null}
        icon={null}
      >
        {submitButtonText
          ? submitButtonText
          : t('user.login.login-button', 'Accedi')}
      </SubmitButton>

      <div style={{ marginTop: '20px', marginBottom: 0 }}>
        {!hideInfos && (
          <>
            <p>
              {t(
                'user.login.sign-in-question',
                'Non sei ancora registrato sul portale?',
              )}{' '}
              <Link to={'/utente/registrazione' as any}>
                {t(
                  'user.login.sign-in-link',
                  'Vai al modulo di registrazione.',
                )}
              </Link>
            </p>
            <p>
              {t(
                'user.login.password-forgot-question',
                'Hai dimenticato la password?',
              )}{' '}
              <Link to={'/utente/recupera-password' as any}>
                {t('user.login.password-forgot-link', 'Reimposta password.')}
              </Link>
            </p>
          </>
        )}
      </div>
    </FormHandler>
  );
}
